import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Animated,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '../../hooks/useTheme';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';

interface CommonHeaderProps {
  title: string;
  subtitle: string;
  showBackButton?: boolean;
  onBackPress?: () => void;
  rightActions?: React.ReactNode;
  animatedStyle?: Record<string, unknown>;
  /** @deprecated Header no longer uses gradient; kept for API compatibility only */
  gradientColors?: string[];
}

const CommonHeader: React.FC<CommonHeaderProps> = ({
  title,
  subtitle,
  showBackButton = false,
  onBackPress,
  rightActions,
  animatedStyle,
}) => {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const { isMobile } = useResponsiveLayout();

  const headerContent = (
    <View
      style={[
        styles.headerInner,
        {
          backgroundColor: colors.card,
          borderColor: colors.border,
        },
      ]}
    >
      <View style={styles.headerContent}>
        {showBackButton && (
          <TouchableOpacity
            style={styles.backButton}
            onPress={onBackPress}
            activeOpacity={0.8}
          >
            <Ionicons
              name="arrow-back"
              size={isMobile ? 20 : 22}
              color={colors.text}
            />
          </TouchableOpacity>
        )}

        <View style={styles.headerText}>
          <Text style={[styles.headerTitle, { color: colors.text }]}>
            {title}
          </Text>
          <Text style={[styles.headerSubtitle, { color: colors.secondary }]}>
            {subtitle}
          </Text>
        </View>

        {rightActions && (
          <View style={styles.headerActions}>{rightActions}</View>
        )}
      </View>
    </View>
  );

  if (animatedStyle) {
    return (
      <Animated.View
        style={[
          styles.header,
          {
            paddingTop: insets.top,
          },
          animatedStyle,
        ]}
      >
        {headerContent}
      </Animated.View>
    );
  }

  return (
    <View
      style={[
        styles.header,
        {
          paddingTop: insets.top,
        },
      ]}
    >
      {headerContent}
    </View>
  );
};

const styles = StyleSheet.create({
  header: {
    marginBottom: 16,
    width: '100%',
  },
  headerInner: {
    padding: 24,
    width: '100%',
    borderBottomWidth: 1,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    width: '100%',
  },
  backButton: {
    padding: 8,
  },
  headerText: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '800',
    marginBottom: 4,
  },
  headerSubtitle: {
    fontSize: 14,
    lineHeight: 20,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
});

export default CommonHeader;
